import { osnovaApi } from '../base';
import { auditBase } from '../types';
import type { PtoProject } from '../types';
import type { AuditEntry, AuditFinding, AuditScope, AuditSession, Claim, ClaimDetails, ClaimDirection, ClaimDocument, ClaimRisk, ClaimType, ComposedDocDetails, Contract, ContractBrief, ContractDetails, ContractTemplate, ContractVersionDiff, ContractVersionFile, Correspondence, CorrespondenceDetails, Counterparty, CounterpartyDetails, CounterpartyDocument, DashboardSnapshot, Deadline, EntityCounterpartyEnriched, GovRequest, Lawsuit, LnaRecord, NpaDigestCounts, NpaEvent, RequisiteCheck, RiskDispatchTarget, RiskNotification, StatutMetrics } from '@entities/legal/types';

export const statutApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    // ===== Correspondence =====
    listCorrespondence: b.query<
      { items: Correspondence[]; total: number; limit: number; offset: number },
      {
        status?: string;
        source?: string;
        search?: string;
        sort?: string;
        order?: 'asc' | 'desc';
        /** Фильтр по контрагенту — вся переписка одного юр.лица.
         *  Используется на странице контрагента «всё по контрагенту». */
        counterparty_id?: string;
        /** Фильтр по договору — вся переписка, привязанная к одному
         *  договору. Используется в секции «Переписка» карточки договора. */
        contract_id?: string;
        /** По умолчанию реестр скрывает возвращённые письма. Передайте true
         *  чтобы увидеть весь поток (для аудита и разбора ошибок). */
        include_returned?: boolean;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (p) => ({
        url: '/agents/statut/correspondence',
        params: p ?? undefined,
      }),
      providesTags: ['Correspondence'],
    }),
    getCorrespondence: b.query<Correspondence, string>({
      query: (id) => `/agents/statut/correspondence/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Correspondence', id }],
    }),
    getCorrespondenceDetails: b.query<CorrespondenceDetails, string>({
      query: (id) => `/agents/statut/correspondence/${id}/details`,
      providesTags: (_r, _e, id) => [
        { type: 'Correspondence', id },
        { type: 'Deadline', id: `for-corr-${id}` },
      ],
    }),
    intakeCorrespondence: b.mutation<
      Correspondence,
      {
        sender?: string;
        subject?: string;
        content_text?: string;
        original_file_hash?: string;
        dispatcher_classification?: Record<string, unknown>;
      }
    >({
      query: (body) => ({
        url: '/agents/statut/correspondence/intake',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Correspondence'],
    }),
    returnCorrespondence: b.mutation<
      { ok: boolean },
      {
        id: string;
        reason: string;
        expected_department?: string;
        dispatcher_department?: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/statut/correspondence/${id}/return-to-dispatcher`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Correspondence'],
    }),
    regenerateCorrespondenceDraft: b.mutation<
      { draft_response: string | null; draft_generated_at: string | null },
      string
    >({
      query: (id) => ({
        url: `/agents/statut/correspondence/${id}/regenerate-draft`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Correspondence', id }, 'Correspondence'],
    }),
    /** Отмена ошибочного авто-возврата диспетчеру. Сбрасывает флаг
     *  returned_to_dispatcher и кладёт письмо обратно в очередь анализа —
     *  LLM прогонит классификацию заново. */
    unreturnCorrespondence: b.mutation<{ ok: boolean; status: string }, string>({
      query: (id) => ({
        url: `/agents/statut/correspondence/${id}/unreturn`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Correspondence', id }, 'Correspondence'],
    }),
    /** Привязка / отвязка письма к договору (HITL). `contractId === null`
     *  отвязывает (body `{ contract_id: null }`). Backend пишет contract_id
     *  в correspondence и возвращает обновлённую запись. Инвалидируем как
     *  само письмо, так и список (фильтр по договору) и затронутый договор
     *  (его секция «Переписка» пересоберётся). */
    setCorrespondenceContract: b.mutation<
      Correspondence,
      { id: string; contractId: string | null }
    >({
      query: ({ id, contractId }) => ({
        url: `/agents/statut/correspondence/${id}/contract`,
        method: 'PUT',
        body: { contract_id: contractId },
      }),
      invalidatesTags: (_r, _e, { id, contractId }) => [
        { type: 'Correspondence', id },
        'Correspondence',
        ...(contractId
          ? [{ type: 'Contract' as const, id: contractId }]
          : []),
      ],
    }),

    // ===== Contracts =====
    listContracts: b.query<
      { items: Contract[]; total: number; limit: number; offset: number },
      {
        search?: string;
        status?: string;
        contract_type?: string;
        counterparty_id?: string;
        /** Вид реестра: `contract` (основной, по умолчанию на backend) или
         *  `precontract` (преддоговорная работа). Разные значения дают разные
         *  ключи кэша RTKQ, поэтому реестры «Договоры» и «Преддоговорная
         *  работа» не перетирают друг друга под общим тегом 'Contract'. */
        kind?: 'contract' | 'precontract';
        /** Ось «Архив» (миграция 0210): `true` — вкладка «Архив»
         *  (заархивированные преддоговоры), иначе рабочий реестр. Разные
         *  значения — разные ключи кэша, поэтому «В работе» и «Архив» не
         *  перетирают друг друга. */
        archived?: boolean;
        /** `created_at | counterparty | sum | expires_at | status` */
        sort?: string;
        /** `asc | desc` */
        order?: string;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (p) => ({
        url: '/agents/statut/contracts',
        params: p ?? undefined,
      }),
      providesTags: ['Contract'],
    }),
    getContract: b.query<Contract, string>({
      query: (id) => `/agents/statut/contracts/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Contract', id }],
    }),
    getContractDetails: b.query<ContractDetails, string>({
      query: (id) => `/agents/statut/contracts/${id}/details`,
      providesTags: (_r, _e, id) => [
        { type: 'Contract', id },
        { type: 'Claim', id: `for-contract-${id}` },
        { type: 'Deadline', id: `for-contract-${id}` },
      ],
    }),
    createContract: b.mutation<
      Contract,
      {
        counterparty: string;
        counterparty_id?: string;
        subject?: string;
        sum?: number;
        currency?: string;
        signed_at?: string;
        expires_at?: string;
        contract_type?: string;
        /** `contract` | `precontract` — в какой реестр создаётся запись.
         *  По умолчанию backend трактует отсутствие как 'contract'. */
        record_kind?: 'contract' | 'precontract';
      }
    >({
      query: (body) => ({
        url: '/agents/statut/contracts',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Contract'],
    }),
    uploadContract: b.mutation<
      { contract_id: string; version_id: string; status: string },
      FormData
    >({
      query: (form) => ({
        url: '/agents/statut/contracts/upload',
        method: 'POST',
        body: form,
      }),
      invalidatesTags: ['Contract'],
    }),
    /**
     * Пачка файлов в уже созданную версию — комплект, не проходящий одним
     * запросом, догружается по частям. Последняя пачка идёт с `finalize=1`,
     * она и запускает разбор.
     */
    appendFilesToVersion: b.mutation<
      { version_id: string; added: number; skipped_duplicates: number; total: number },
      { contractId: string; versionId: string; form: FormData }
    >({
      query: ({ contractId, versionId, form }) => ({
        url: `/agents/statut/contracts/${contractId}/versions/${versionId}/files`,
        method: 'POST',
        body: form,
      }),
      // Реестр перезапрашиваем только после финальной пачки — иначе каждая
      // из десятка промежуточных дёргала бы перезагрузку списка.
      invalidatesTags: (_r, _e, { contractId, form }) =>
        form.get('finalize') === '1' ? [{ type: 'Contract', id: contractId }, 'Contract'] : [],
    }),
    uploadContractVersion: b.mutation<
      { version_id: string; version_no: number; status: string },
      { contractId: string; form: FormData }
    >({
      query: ({ contractId, form }) => ({
        url: `/agents/statut/contracts/${contractId}/versions`,
        method: 'POST',
        body: form,
      }),
      invalidatesTags: (_r, _e, { contractId }) => [
        { type: 'Contract', id: contractId },
        'Contract',
      ],
    }),
    /** Удаление одной версии договора. Каскадно убирает файлы версии и её
     *  риски; если удалена latest-версия — latest становится предыдущая
     *  (или договор без версий). Инвалидируем тот же тег, что и загрузка
     *  версии, чтобы карточка перечитала versions + risks + latest. */
    deleteContractVersion: b.mutation<
      { ok: boolean; deleted_version_id: string },
      { contractId: string; versionId: string }
    >({
      query: ({ contractId, versionId }) => ({
        url: `/agents/statut/contracts/${contractId}/versions/${versionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { contractId }) => [
        { type: 'Contract', id: contractId },
        'Contract',
      ],
    }),
    /** Догрузка файлов в новую версию договора (multipart с N file).
     *  Создаёт НОВУЮ версию (version_no+1), не плодит договоров. Backend
     *  ставит её в очередь анализа сразу же. */
    uploadFilesToContract: b.mutation<
      // Обычная догрузка → { version_id, version_no, status }; «приложить без
      // анализа» (FormData содержит no_analysis=1) → { attached, added,
      // skipped_duplicates, total }. Поля опциональны — режим выбирает форма.
      {
        version_id?: string;
        version_no?: number;
        status?: string;
        attached?: boolean;
        added?: number;
        skipped_duplicates?: number;
        total?: number;
      },
      { contractId: string; form: FormData }
    >({
      query: ({ contractId, form }) => ({
        url: `/agents/statut/contracts/${contractId}/files`,
        method: 'POST',
        body: form,
      }),
      invalidatesTags: (_r, _e, { contractId }) => [
        { type: 'Contract', id: contractId },
        { type: 'ContractFiles', id: contractId },
        'Contract',
      ],
    }),
    /** Список файлов конкретной версии договора. Возвращает порядок
     *  (ordinal) — основной/приложение/спецификация и т.п. */
    listContractVersionFiles: b.query<
      { items: ContractVersionFile[] },
      { contractId: string; versionId: string }
    >({
      query: ({ contractId, versionId }) =>
        `/agents/statut/contracts/${contractId}/versions/${versionId}/files`,
      providesTags: (_r, _e, { contractId }) => [{ type: 'ContractFiles', id: contractId }],
    }),
    /** Ручное удаление одного файла из комплекта версии договора. Backend
     *  снимает запись и сам файл, 404 если файла нет. Инвалидируем тот же
     *  тег, что отдаёт listContractVersionFiles, чтобы таблица перечиталась. */
    deleteContractVersionFile: b.mutation<
      { ok: boolean },
      { contractId: string; versionId: string; fileId: string }
    >({
      query: ({ contractId, versionId, fileId }) => ({
        url: `/agents/statut/contracts/${contractId}/versions/${versionId}/files/${fileId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { contractId }) => [
        { type: 'ContractFiles', id: contractId },
      ],
    }),
    /** Ручная смена типа документа юристом. Тип решает, попадёт ли файл в
     *  промпт юр-агента, поэтому правка человека — страховка от ошибки
     *  классификатора. Backend ставит kind_source='lawyer' — последующие
     *  перепарсы комплекта это решение не затрут. Анализ не перезапускается:
     *  юрист правит типы пачкой и запускает разбор сам. */
    setContractVersionFileKind: b.mutation<
      { ok: boolean; document_kind: string; kind_source: string },
      { contractId: string; versionId: string; fileId: string; documentKind: string }
    >({
      query: ({ contractId, versionId, fileId, documentKind }) => ({
        url: `/agents/statut/contracts/${contractId}/versions/${versionId}/files/${fileId}`,
        method: 'PATCH',
        body: { document_kind: documentKind },
      }),
      invalidatesTags: (_r, _e, { contractId }) => [
        { type: 'ContractFiles', id: contractId },
      ],
    }),
    /** Список упомянутых сторон договора (LLM-extracted ИНН → enriched). */
    listContractParties: b.query<{ items: EntityCounterpartyEnriched[] }, string>({
      query: (contractId) => `/agents/statut/contracts/${contractId}/parties`,
      providesTags: (_r, _e, contractId) => [{ type: 'Parties', id: `${contractId}:contract` }],
    }),
    analyzeContract: b.mutation<{ ok: boolean; version_id: string }, string>({
      query: (id) => ({
        url: `/agents/statut/contracts/${id}/analyze`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Contract', id }],
    }),
    /** Запуск анализа гигантского договора (status='manual_review' →
     *  'under_review'). Обходит sanity-limit на N окон, юрист сознательно
     *  жмёт после предупреждения о стоимости. Используется для документов
     *  6+ МБ / 200+ окон. */
    forceAnalyzeContract: b.mutation<{ ok: boolean; version_id: string }, string>({
      query: (id) => ({
        url: `/agents/statut/contracts/${id}/force-analyze`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Contract', id }],
    }),
    /** Остановить текущий анализ договора (status 'under_review'/'parsing' →
     *  'cancelled'). Backend помечает активную версию как отменённую; юрист
     *  потом может перезапустить через analyzeContract/forceAnalyzeContract. */
    cancelContractAnalysis: b.mutation<
      { ok: boolean; version_id: string; status: string },
      string
    >({
      query: (id) => ({
        url: `/agents/statut/contracts/${id}/cancel-analysis`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Contract', id }, 'Contract'],
    }),
    diffContractVersions: b.query<
      ContractVersionDiff,
      { contractId: string; from: string; to: string }
    >({
      query: ({ contractId, from, to }) =>
        `/agents/statut/contracts/${contractId}/diff?from=${from}&to=${to}`,
    }),
    listContractTemplates: b.query<{ items: ContractTemplate[] }, void>({
      query: () => '/agents/statut/contract-templates',
      providesTags: ['ContractTemplate'],
    }),
    createContractTemplate: b.mutation<
      ContractTemplate,
      {
        contract_type: string;
        name: string;
        content_text: string;
        description?: string;
      }
    >({
      query: (body) => ({
        url: '/agents/statut/contract-templates',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ContractTemplate'],
    }),
    uploadContractTemplate: b.mutation<ContractTemplate, FormData>({
      query: (form) => ({
        url: '/agents/statut/contract-templates/upload',
        method: 'POST',
        body: form,
      }),
      invalidatesTags: ['ContractTemplate'],
    }),
    deleteContractTemplate: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/statut/contract-templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ContractTemplate'],
    }),
    // Контрагенты
    listCounterparties: b.query<
      {
        items: Counterparty[];
        total: number;
        limit: number;
        offset: number;
        egrul_enabled: boolean;
      },
      {
        status?: string;
        party_type?: string;
        search?: string;
        sort?: string;
        order?: 'asc' | 'desc';
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (p) => ({
        url: '/agents/statut/counterparties',
        params: p ?? undefined,
      }),
      providesTags: ['Counterparty'],
    }),
    getCounterparty: b.query<Counterparty, string>({
      query: (id) => `/agents/statut/counterparties/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Counterparty', id }],
    }),
    getCounterpartyDetails: b.query<CounterpartyDetails, string>({
      query: (id) => `/agents/statut/counterparties/${id}/details`,
      providesTags: (_r, _e, id) => [
        { type: 'Counterparty', id },
        { type: 'Contract', id: `for-counterparty-${id}` },
      ],
    }),
    createCounterparty: b.mutation<
      Counterparty,
      { inn: string; full_name?: string; auto_lookup?: boolean }
    >({
      query: (body) => ({
        url: '/agents/statut/counterparties',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Counterparty'],
    }),
    refreshCounterpartyEgrul: b.mutation<Counterparty, string>({
      query: (id) => ({
        url: `/agents/statut/counterparties/${id}/refresh-egrul`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Counterparty', id }, 'Counterparty'],
    }),
    // РНП + ФССП параллельно. Результаты в risk_flags.rnp/fssp.
    refreshCounterpartyExternal: b.mutation<Counterparty, string>({
      query: (id) => ({
        url: `/agents/statut/counterparties/${id}/refresh-external`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Counterparty', id }, 'Counterparty'],
    }),
    // карта рисков по контрагенту: агрегат по категориям/severity.
    getCounterpartyRiskMap: b.query<
      {
        counterparty_id: string;
        contracts_count: number;
        total_risks: number;
        total_critical: number;
        total_major: number;
        total_minor: number;
        categories: Array<{
          category: string;
          critical: number;
          major: number;
          minor: number;
          total: number;
        }>;
      },
      string
    >({
      query: (id) => `/agents/statut/counterparties/${id}/risk-map`,
      providesTags: (_r, _e, id) => [
        { type: 'Counterparty', id },
        { type: 'Contract', id: `risks-for-${id}` },
      ],
    }),
    updateCounterpartyNotes: b.mutation<{ ok: boolean }, { id: string; notes: string | null }>({
      query: ({ id, notes }) => ({
        url: `/agents/statut/counterparties/${id}/notes`,
        method: 'POST',
        body: { notes },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Counterparty', id }],
    }),
    /** Уставные документы контрагента (устав, приказы, доверенности, карточка
     *  предприятия, документы юр. адреса). Отдельный репозиторий карточки —
     *  не путать с версиями договора и документами претензии. */
    getCounterpartyDocuments: b.query<{ items: CounterpartyDocument[] }, string>({
      query: (counterpartyId) => `/agents/statut/counterparties/${counterpartyId}/documents`,
      providesTags: (_r, _e, counterpartyId) => [
        { type: 'CounterpartyDocuments', id: counterpartyId },
      ],
    }),
    /** Догрузка N уставных документов (multipart: N полей `file` + `doc_type`
     *  + опц. `valid_until` YYYY-MM-DD). Backend возвращает список созданных
     *  записей; UI не полагается на его форму — после успеха инвалидирует тег
     *  и перечитывает список. */
    uploadCounterpartyDocuments: b.mutation<
      CounterpartyDocument[],
      { counterpartyId: string; form: FormData }
    >({
      query: ({ counterpartyId, form }) => ({
        url: `/agents/statut/counterparties/${counterpartyId}/documents`,
        method: 'POST',
        body: form,
      }),
      // Догрузка документа меняет базу для сверки реквизитов — сбрасываем
      // и её кеш, чтобы кнопка «Проверить реквизиты» перечитала свежий срез.
      invalidatesTags: (_r, _e, { counterpartyId }) => [
        { type: 'CounterpartyDocuments', id: counterpartyId },
        { type: 'CounterpartyRequisiteCheck', id: counterpartyId },
      ],
    }),
    /** Мягкое удаление одного уставного документа. URL не вложен в id
     *  контрагента (`/counterparties/documents/{docId}`), поэтому в аргумент
     *  дополнительно кладём counterpartyId — для инвалидации нужного тега. */
    deleteCounterpartyDocument: b.mutation<
      { ok: boolean },
      { counterpartyId: string; docId: string }
    >({
      query: ({ docId }) => ({
        url: `/agents/statut/counterparties/documents/${docId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { counterpartyId }) => [
        { type: 'CounterpartyDocuments', id: counterpartyId },
        { type: 'CounterpartyRequisiteCheck', id: counterpartyId },
      ],
    }),
    /** Сверка реквизитов контрагента с загруженными уставными документами
     *  (директор/адрес/ОГРН и т.п.). Запрашивается по кнопке на карточке —
     *  отсюда отдельный тег `CounterpartyRequisiteCheck`, который сбрасывают
     *  загрузка/удаление документа. Результат — ИНФОРМАЦИОННЫЙ сигнал под
     *  решение юриста, не автоматический вывод. */
    getCounterpartyRequisiteCheck: b.query<RequisiteCheck, string>({
      query: (counterpartyId) =>
        `/agents/statut/counterparties/${counterpartyId}/requisite-check`,
      providesTags: (_r, _e, counterpartyId) => [
        { type: 'CounterpartyRequisiteCheck', id: counterpartyId },
      ],
    }),
    getStatutMetrics: b.query<StatutMetrics, void>({
      query: () => '/agents/statut/metrics',
    }),
    getContractAudit: b.query<{ items: AuditEntry[] }, string>({
      query: (contractId) => `/agents/statut/contracts/${contractId}/audit`,
      providesTags: (_r, _e, id) => [{ type: 'Contract', id }],
    }),
    lookupCounterpartyByInn: b.query<
      {
        source: 'local' | 'egrul_available' | 'none';
        counterparty: Counterparty | null;
        egrul_enabled?: boolean;
        hint?: string;
      },
      string
    >({
      query: (inn) => `/agents/statut/counterparties/lookup-inn?inn=${encodeURIComponent(inn)}`,
    }),
    annotateRisk: b.mutation<
      { risks: import('@entities/legal/types').ContractRisk[] },
      {
        contractId: string;
        riskId: string;
        decision: 'accepted' | 'disputed' | 'rejected';
        comment?: string;
      }
    >({
      query: ({ riskId, decision, comment }) => ({
        url: `/agents/statut/contracts/risks/${riskId}/annotate`,
        method: 'POST',
        body: { decision, comment },
      }),
      // Оптимистичный апдейт — UI меняется СРАЗУ, не дожидаясь сервера.
      // НЕ инвалидируем теги: лишний refetch только мерцал бы пользователю,
      // а кэш мы уже патчим вручную. Сервер вернёт обновлённый список —
      // если что-то разошлось, следующий polling карточки выровняет.
      async onQueryStarted(
        { contractId, riskId, decision, comment },
        { dispatch, queryFulfilled },
      ) {
        const patch = dispatch(
          statutApi.util.updateQueryData('getContractDetails', contractId, (draft) => {
            const r = draft.risks.find((x) => x.id === riskId);
            if (r) {
              r.decision = decision;
              r.decision_comment = comment ?? null;
              r.decision_at = new Date().toISOString();
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    clearRiskAnnotation: b.mutation<
      { risks: import('@entities/legal/types').ContractRisk[] },
      { contractId: string; riskId: string }
    >({
      query: ({ riskId }) => ({
        url: `/agents/statut/contracts/risks/${riskId}/annotate`,
        method: 'DELETE',
      }),
      async onQueryStarted({ contractId, riskId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          statutApi.util.updateQueryData('getContractDetails', contractId, (draft) => {
            const r = draft.risks.find((x) => x.id === riskId);
            if (r) {
              r.decision = null;
              r.decision_comment = null;
              r.decision_at = null;
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    /** HITL: юрист добавляет свой риск (LLM пропустила). source='manual'. */
    addManualContractRisk: b.mutation<
      { risks: import('@entities/legal/types').ContractRisk[] },
      {
        contractId: string;
        category: string;
        severity: 'critical' | 'major' | 'minor';
        description: string;
        proposed_change?: string;
      }
    >({
      query: ({ contractId, ...body }) => ({
        url: `/agents/statut/contracts/${contractId}/risks`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Contract', id: arg.contractId }],
    }),
    /** HITL: правка текста риска юристом. source: llm → lawyer_corrected. */
    patchContractRisk: b.mutation<
      { risks: import('@entities/legal/types').ContractRisk[] },
      {
        contractId: string;
        riskId: string;
        category?: string;
        severity?: 'critical' | 'major' | 'minor';
        description?: string;
        proposed_change?: string;
      }
    >({
      query: ({ contractId: _c, riskId, ...body }) => ({
        url: `/agents/statut/contracts/risks/${riskId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Contract', id: arg.contractId }],
    }),
    /** HITL: удалить риск (ошибочно добавленный или неактуальный). */
    deleteContractRisk: b.mutation<
      { risks: import('@entities/legal/types').ContractRisk[] },
      { contractId: string; riskId: string }
    >({
      query: ({ riskId }) => ({
        url: `/agents/statut/contracts/risks/${riskId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Contract', id: arg.contractId }],
    }),
    /** HITL bulk: разметка N рисков одним decision'ом. */
    bulkAnnotateContractRisks: b.mutation<
      {
        risks: import('@entities/legal/types').ContractRisk[];
        processed: number;
        errors: number;
      },
      {
        contractId: string;
        riskIds: string[];
        decision: 'accepted' | 'disputed' | 'rejected';
        comment?: string;
      }
    >({
      query: ({ contractId, riskIds, decision, comment }) => ({
        url: `/agents/statut/contracts/${contractId}/risks/bulk-annotate`,
        method: 'POST',
        body: { risk_ids: riskIds, decision, comment },
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Contract', id: arg.contractId }],
    }),
    patchContract: b.mutation<
      Contract,
      {
        id: string;
        counterparty?: string;
        subject?: string;
        sum?: number;
        currency?: string;
        signed_at?: string | null;
        expires_at?: string | null;
        contract_type?: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/statut/contracts/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Contract', id: arg.id }, 'Contract'],
    }),
    /** Свободная заметка юриста на карточке договора (миграция 0211): произвольный
     *  текст о состоянии дела (где оригиналы, статус подписания и т.п.). Пустая
     *  строка / null очищает заметку. Возвращает обновлённый договор;
     *  инвалидируем карточку (getContractDetails / getContract). */
    updateContractNote: b.mutation<Contract, { id: string; note: string | null }>({
      query: ({ id, note }) => ({
        url: `/agents/statut/contracts/${id}/notes`,
        method: 'POST',
        body: { note },
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Contract', id: arg.id }, 'Contract'],
    }),
    /** Задать / очистить номер договора (`contract_number: null` очищает).
     *  Сохранение всегда успешно; `duplicate_warning` — информационный
     *  блок «у этого контрагента уже есть договор(ы) с таким номером»,
     *  не блокирует. Инвалидируем сам договор и реестр (номер участвует
     *  в поиске и в колонке списка). */
    setContractNumber: b.mutation<
      {
        contract: Contract;
        duplicate_warning: {
          count: number;
          duplicates: Array<{
            id: string;
            contract_number: string | null;
            subject: string | null;
            signed_at: string | null;
          }>;
        } | null;
      },
      { id: string; contractNumber: string | null }
    >({
      query: ({ id, contractNumber }) => ({
        url: `/agents/statut/contracts/${id}/number`,
        method: 'PUT',
        body: { contract_number: contractNumber },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Contract', id }, 'Contract'],
    }),
    /** Переписка, привязанная к договору (для секции «Переписка» в карточке
     *  договора). Зеркалит GET /contracts/:id/correspondence. */
    getContractCorrespondence: b.query<Correspondence[], string>({
      query: (contractId) => `/agents/statut/contracts/${contractId}/correspondence`,
      providesTags: (_r, _e, contractId) => [
        { type: 'Correspondence', id: `for-contract-${contractId}` },
        'Correspondence',
      ],
    }),
    deleteContract: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/statut/contracts/${id}`,
        method: 'DELETE',
      }),
      // Сроки с scope='contract' и entity_id=<id> удаляются в БД той же
      // транзакцией — инвалидируем реестр сроков, чтоб они исчезли из UI.
      invalidatesTags: (_r, _e, id) => [
        'Contract',
        'Deadline',
        { type: 'Deadline', id: `for-contract-${id}` },
      ],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        // Оптимистично убираем из списка договоров
        const patch = dispatch(
          statutApi.util.updateQueryData('listContracts', undefined, (draft) => {
            draft.items = draft.items.filter((c) => c.id !== id);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    /** Заключение договора: оставляет преддоговор в его реестре (status=
     *  'concluded') и создаёт ОТДЕЛЬНЫЙ договор в «Договорах» (чистая карточка
     *  для подписанного скана). Возвращает обе строки. Инвалидируем общий тег
     *  'Contract' — оба реестра («Договоры» / «Преддоговорная работа») и
     *  дашборд перечитаются; по id (преддоговор) — карточку с бейджем-ссылкой. */
    concludeContract: b.mutation<
      { precontract: Contract; contract: Contract },
      string
    >({
      query: (id) => ({
        url: `/agents/statut/contracts/${id}/conclude`,
        method: 'PATCH',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Contract', id }, 'Contract'],
    }),
    /** Вернуть преддоговор из архива (миграция 0210) в рабочий реестр
     *  «Преддоговорная работа». Инвалидируем общий тег 'Contract' — и вкладка
     *  «Архив», и «В работе», и дашборд перечитаются. */
    unarchiveContract: b.mutation<Contract, string>({
      query: (id) => ({
        url: `/agents/statut/contracts/${id}/unarchive`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Contract', id }, 'Contract'],
    }),

    // ===== Claims =====
    listClaims: b.query<
      { items: Claim[]; total: number; limit: number; offset: number },
      {
        claim_type?: ClaimType;
        stage?: string;
        direction?: 'incoming' | 'outgoing';
        /** Фильтр по контрагенту — все претензии/иски одного юр.лица.
         *  Используется на странице контрагента «всё по контрагенту». */
        counterparty_id?: string;
        search?: string;
        sort?: string;
        order?: 'asc' | 'desc';
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (p) => ({
        url: '/agents/statut/claims',
        params: p ?? undefined,
      }),
      providesTags: ['Claim'],
    }),
    getClaim: b.query<Claim, string>({
      query: (id) => `/agents/statut/claims/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Claim', id }],
    }),
    getClaimDetails: b.query<ClaimDetails, string>({
      query: (id) => `/agents/statut/claims/${id}/details`,
      providesTags: (_r, _e, id) => [{ type: 'Claim', id }],
    }),
    listClaimRisks: b.query<{ items: ClaimRisk[] }, string>({
      query: (id) => `/agents/statut/claims/${id}/risks`,
      providesTags: (_r, _e, id) => [{ type: 'Claim', id }],
    }),
    updateClaimStage: b.mutation<{ ok: boolean }, { id: string; stage: string }>({
      query: ({ id, stage }) => ({
        url: `/agents/statut/claims/${id}/stage`,
        method: 'POST',
        body: { stage },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Claim', id }, 'Claim'],
    }),
    createClaim: b.mutation<
      Claim,
      {
        claim_type: ClaimType;
        counterparty: string;
        dispute_type?: string;
        contract_id?: string;
        amount?: number;
        forecast?: string;
        direction?: 'incoming' | 'outgoing';
      }
    >({
      query: (body) => ({
        url: '/agents/statut/claims',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Claim'],
    }),
    /** Загрузка файлов претензии/иска с запуском двухветочного LLM-анализа.
     *  Принимает массив File — первый «основной», остальные приложения.
     *  Возвращает `claim_id` — UI открывает карточку и подписывается на WS. */
    uploadClaim: b.mutation<
      { claim_id: string; direction: string; status: string },
      {
        files: File[];
        claim_type: ClaimType;
        direction: 'incoming' | 'outgoing';
        counterparty: string;
        counterparty_id?: string;
        dispute_type?: string;
        contract_id?: string;
      }
    >({
      query: ({
        files,
        claim_type,
        direction,
        counterparty,
        counterparty_id,
        dispute_type,
        contract_id,
      }) => {
        const form = new FormData();
        for (const f of files) {
          form.append('file', f);
        }
        form.append('claim_type', claim_type);
        form.append('direction', direction);
        form.append('counterparty', counterparty);
        if (counterparty_id) form.append('counterparty_id', counterparty_id);
        if (dispute_type) form.append('dispute_type', dispute_type);
        if (contract_id) form.append('contract_id', contract_id);
        return {
          url: '/agents/statut/claims/upload',
          method: 'POST',
          body: form,
        };
      },
      invalidatesTags: ['Claim'],
    }),
    reanalyzeClaim: b.mutation<{ ok: boolean; status: string }, string>({
      query: (id) => ({
        url: `/agents/statut/claims/${id}/reanalyze`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Claim', id }, 'Claim'],
    }),
    /** Исправить направление (наш документ завели входящим или наоборот).
     *  Backend пишет смену в журнал и перезапускает анализ нужным промптом. */
    setClaimDirection: b.mutation<
      { ok: boolean; direction: ClaimDirection },
      { id: string; direction: ClaimDirection }
    >({
      query: ({ id, direction }) => ({
        url: `/agents/statut/claims/${id}/direction`,
        method: 'POST',
        body: { direction },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Claim', id }, 'Claim'],
    }),
    /** Привязка договора к претензии/иску. Backend пишет contract_id в Claim
     *  и автоматически ставит analysis_status='processing' + запускает
     *  re-analyze. Юрист увидит обновлённые риски через WS claim_analyzed. */
    linkClaimContract: b.mutation<
      { ok: boolean; status: string },
      { claimId: string; contractId: string }
    >({
      query: ({ claimId, contractId }) => ({
        url: `/agents/statut/claims/${claimId}/link-contract`,
        method: 'POST',
        body: { contract_id: contractId },
      }),
      invalidatesTags: (_r, _e, { claimId }) => [{ type: 'Claim', id: claimId }, 'Claim'],
    }),
    /** Снять привязку договора с претензии. Backend сбрасывает contract_id
     *  в NULL и запускает повторный анализ — LLM перенализирует претензию
     *  без contract-блока (только нормы и практика). */
    unlinkClaimContract: b.mutation<{ ok: boolean; status: string }, string>({
      query: (claimId) => ({
        url: `/agents/statut/claims/${claimId}/link-contract`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, claimId) => [{ type: 'Claim', id: claimId }, 'Claim'],
    }),
    /** Stage G: перевыставление входящей претензии на субподрядчика.
     *  Backend генерирует LLM текст новой исходящей претензии в порядке
     *  регресса, **не** сохраняя в БД — только preview. Длится 15-40 сек. */
    redirectClaimPreview: b.mutation<
      {
        text: string;
        summary: string;
        main_contract_no: string | null;
        subcontract_no: string | null;
        original_claim_id: string;
        subcontractor_id: string;
        subcontract_id: string;
      },
      { claimId: string; subcontractorId: string; subcontractId: string }
    >({
      query: ({ claimId, subcontractorId, subcontractId }) => ({
        url: `/agents/statut/claims/${claimId}/redirect-preview`,
        method: 'POST',
        body: {
          subcontractor_id: subcontractorId,
          subcontract_id: subcontractId,
        },
      }),
      // preview не меняет БД — кэш не инвалидируем.
    }),
    /** Stage E ADR-0001: ставит в очередь async-job ComposeClaimRedirect.
     *  Сразу возвращает `{status:'queued'}`. Worker через 30-60с шлёт
     *  WS-event claim_response_composed → фронт через rtkBridge сам
     *  инвалидирует ComposedResponses-тег и юрист видит готовый DOCX
     *  в карточке претензии — даже если успел закрыть модалку. */
    composeClaimRedirect: b.mutation<
      { status: 'queued' },
      { claimId: string; subcontractorId: string; subcontractId: string }
    >({
      query: ({ claimId, subcontractorId, subcontractId }) => ({
        url: `/agents/statut/claims/${claimId}/compose-redirect`,
        method: 'POST',
        body: {
          subcontractor_id: subcontractorId,
          subcontract_id: subcontractId,
        },
      }),
    }),
    /** Stage G ADR-0001: async-job ComposeContractProtocol — DOCX
     *  протокола разногласий по договору. WS-event
     *  contract_protocol_composed → rtkBridge инвалидирует
     *  ComposedResponses:contract:{id}. */
    composeContractProtocol: b.mutation<{ status: 'queued' }, string>({
      query: (contractId) => ({
        url: `/agents/statut/contracts/${contractId}/compose-protocol`,
        method: 'POST',
      }),
    }),
    /** Запустить рассылку рисков договора по отделам (async job). `route_ids` —
     *  выбранные ящики отделов (mailbox_routes). WS-event
     *  department_risks_dispatched. */
    dispatchContractRisks: b.mutation<
      { status: 'queued' },
      { contractId: string; route_ids: string[]; comment?: string | null }
    >({
      query: ({ contractId, route_ids, comment }) => ({
        url: `/agents/statut/contracts/${contractId}/dispatch-department-risks`,
        method: 'POST',
        body: { route_ids, comment },
      }),
    }),
    /** In-app уведомления текущего пользователя о присланных ему рисках отдела.
     *  Доступно любому авторизованному (сотрудник отдела может не иметь доступа
     *  к агенту Юрист). */
    listRiskNotifications: b.query<{ items: RiskNotification[] }, void>({
      query: () => '/agents/statut/risk-notifications',
      providesTags: ['RiskNotification'],
    }),
    /** Отметить карточку риск-уведомления показанной (по target_id). */
    markRiskNotificationSeen: b.mutation<{ ok: boolean }, string>({
      query: (targetId) => ({
        url: `/agents/statut/risk-notifications/${targetId}/seen`,
        method: 'POST',
      }),
      invalidatesTags: ['RiskNotification'],
    }),
    /** История DOCX-черновиков протокола разногласий для договора. */
    listComposedForContract: b.query<
      {
        items: Array<{
          id: string;
          claim_id: string | null;
          contract_id: string | null;
          template_id: string | null;
          size_bytes: number;
          source_similarity: number | null;
          llm_model: string | null;
          content_hash: string;
          created_at: string;
          /** Тип шаблона из knowledge_documents.metadata. Для протоколов
           *  всегда 'protocol_disagreement', null если шаблон удалён. */
          source_template_kind: string | null;
        }>;
      },
      string
    >({
      query: (contractId) =>
        `/agents/statut/contracts/${contractId}/composed-protocols`,
      providesTags: (_r, _e, contractId) => [
        { type: 'ComposedResponses', id: `contract:${contractId}` },
      ],
    }),
    /** Stage G: сохранение преподнесённого preview как новой outgoing-
     *  претензии. Backend создаёт claim(kind=Outgoing, redirected_from_id=...),
     *  возвращает её id. UI редиректит на карточку новой претензии. */
    redirectClaimSave: b.mutation<
      { ok: boolean; claim_id: string },
      {
        claimId: string;
        subcontractorId: string;
        subcontractId: string;
        text: string;
        summary: string;
      }
    >({
      query: ({ claimId, subcontractorId, subcontractId, text, summary }) => ({
        url: `/agents/statut/claims/${claimId}/redirect-save`,
        method: 'POST',
        body: {
          subcontractor_id: subcontractorId,
          subcontract_id: subcontractId,
          text,
          summary,
        },
      }),
      invalidatesTags: ['Claim'],
    }),
    /** Догрузка документов в претензию/иск (multipart с N file). Не плодит
     *  новые claim, добавляет в текущий комплект и форсирует re-analyze. */
    uploadDocumentsToClaim: b.mutation<
      {
        added: number;
        items: Array<{ id: string; filename: string; ordinal: number }>;
        status: string;
      },
      { claimId: string; form: FormData }
    >({
      query: ({ claimId, form }) => ({
        url: `/agents/statut/claims/${claimId}/documents`,
        method: 'POST',
        body: form,
      }),
      invalidatesTags: (_r, _e, { claimId }) => [
        { type: 'Claim', id: claimId },
        { type: 'ClaimDocuments', id: claimId },
        'Claim',
      ],
    }),
    /** Список документов комплекта претензии/иска (с ordinal). */
    listClaimDocuments: b.query<{ items: ClaimDocument[] }, string>({
      query: (claimId) => `/agents/statut/claims/${claimId}/documents`,
      providesTags: (_r, _e, claimId) => [{ type: 'ClaimDocuments', id: claimId }],
    }),
    /** Ручное удаление одного документа из комплекта претензии/иска. Backend
     *  снимает запись и файл, 404 если файла нет. Инвалидируем тот же тег,
     *  что отдаёт listClaimDocuments, чтобы таблица перечиталась. */
    deleteClaimDocument: b.mutation<
      { ok: boolean },
      { claimId: string; docId: string }
    >({
      query: ({ claimId, docId }) => ({
        url: `/agents/statut/claims/${claimId}/documents/${docId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { claimId }) => [
        { type: 'ClaimDocuments', id: claimId },
      ],
    }),
    /** ОСНОВА.Шаблонизатор (ADR-0001 Stage D): попросить агента собрать
     *  DOCX-черновик ответа на входящую претензию по шаблонам из KB.
     *  Возвращает либо ok с composed_id, либо fallback с reason. */
    composeClaimResponse: b.mutation<
      | {
          status: 'ok';
          composed_id: string;
          template_id: string;
          similarity: number;
        }
      | { status: 'fallback'; reason: string },
      string
    >({
      query: (claimId) => ({
        url: `/agents/statut/claims/${claimId}/compose-response`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, claimId) => [
        { type: 'ComposedResponses', id: claimId },
      ],
    }),
    /** Удалить сгенерированный DOCX (ответ / перевыставление / протокол).
     *  Backend пошлёт WS `composed_deleted` → rtkBridge инвалидирует
     *  все три тега-провайдера (для карточки и для архива). */
    deleteComposed: b.mutation<{ ok: boolean; deleted_id: string }, string>({
      query: (composedId) => ({
        url: `/agents/statut/composed/${composedId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ComposedResponses'],
    }),
    /** История DOCX-черновиков ответов для одной претензии (от свежих
     *  к старым). Метаданные без blob — скачивание отдельным GET.
     *  source_template_kind позволяет фронту отличить «ответ на претензию»
     *  (response_claim) от «перевыставления на субподрядчика»
     *  (outgoing_claim) — оба пишутся в одну таблицу. */
    listComposedResponses: b.query<
      {
        items: Array<{
          id: string;
          claim_id: string;
          template_id: string | null;
          size_bytes: number;
          source_similarity: number | null;
          llm_model: string | null;
          content_hash: string;
          created_at: string;
          source_template_kind: string | null;
        }>;
      },
      string
    >({
      query: (claimId) =>
        `/agents/statut/claims/${claimId}/composed-responses`,
      providesTags: (_r, _e, claimId) => [
        { type: 'ComposedResponses', id: claimId },
      ],
    }),
    /** Глобальный архив сгенерированных DOCX — для одноимённой страницы.
     *  Фильтр по типу шаблона + paginated. Содержит и ответы на
     *  претензии, и перевыставления, и протоколы разногласий — юрист
     *  в одном месте видит всё, не лазая по карточкам. */
    listComposedArchive: b.query<
      {
        items: Array<{
          id: string;
          claim_id: string | null;
          contract_id: string | null;
          template_id: string | null;
          size_bytes: number;
          source_similarity: number | null;
          llm_model: string | null;
          content_hash: string;
          created_at: string;
          source_template_kind: string | null;
        }>;
        total: number;
        limit: number;
        offset: number;
      },
      { kind?: string; limit?: number; offset?: number } | undefined
    >({
      query: (arg) => {
        const p = new URLSearchParams();
        if (arg?.kind) p.set('kind', arg.kind);
        if (arg?.limit != null) p.set('limit', String(arg.limit));
        if (arg?.offset != null) p.set('offset', String(arg.offset));
        const qs = p.toString();
        return `/agents/statut/composed${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['ComposedResponses'],
    }),
    /** Упомянутые стороны претензии/иска (истец/ответчик/третьи лица). */
    listClaimParties: b.query<{ items: EntityCounterpartyEnriched[] }, string>({
      query: (claimId) => `/agents/statut/claims/${claimId}/parties`,
      providesTags: (_r, _e, claimId) => [{ type: 'Parties', id: `${claimId}:claim` }],
    }),
    /** HITL-разметка риска претензии/иска (accepted/disputed/rejected).
     *  Решение также сохраняется в слой памяти агента (agent_learnings) —
     *  будущие анализы похожих рисков будут учитывать это решение. */
    /** Plain-text пакет для копирования юристом во внешний ИИ-аудит.
     *  scope = 'contract' | 'claim'. Возвращает { text } — готов для
     *  navigator.clipboard.writeText().
     *
     *  ВАЖНО: используется как обычный (не lazy) query — пакет грузится
     *  при открытии модалки, к моменту клика «Скопировать» уже в кэше.
     *  Это позволяет вызвать writeText синхронно в обработчике клика и
     *  не упереться в user-gesture activation в Firefox. */
    getAuditPackage: b.query<{ text: string }, { scope: AuditScope; id: string }>({
      query: ({ scope, id }) => `${auditBase(scope, id)}/audit-package`,
    }),
    /** Старт async-сессии парсинга внешнего аудита. Возвращает session
     *  сразу — парсинг идёт фоном, фронт подписывается на WS-событие
     *  audit_session_updated. Раньше был sync POST /audit/parse, упирался
     *  в nginx-таймаут 60с при долгом LLM-парсинге.
     *
     *  `mode` (optional): 'audit' (по умолчанию на бэке) — текст аудита;
     *  'reference' — текст эталонного документа от другой нейронки по той
     *  же сделке. Передаём в body только когда явно задан, чтобы для
     *  договоров/претензий поведение осталось прежним. */
    startAuditSession: b.mutation<
      AuditSession,
      {
        scope: AuditScope;
        id: string;
        text: string;
        source_model: string;
        mode?: 'audit' | 'reference';
      }
    >({
      query: ({ scope, id, text, source_model, mode }) => ({
        url: `${auditBase(scope, id)}/audit/parse`,
        method: 'POST',
        body: mode ? { text, source_model, mode } : { text, source_model },
      }),
      // ВАЖНО: тег с РЕАЛЬНЫМ scope — rtkBridge инвалидирует
      // AuditSession:{scope}:{entity_id} из WS-события audit_session_updated,
      // где scope приходит как фактический ('protocol_disagreement' и т.п.).
      invalidatesTags: (_r, _e, { scope, id }) => [
        { type: 'AuditSession', id: `${scope}:${id}` },
      ],
    }),
    /** Список сессий аудита для документа — нужен при открытии модалки,
     *  чтобы восстановить состояние (идёт парсинг? готовые findings?). */
    listAuditSessions: b.query<
      { items: AuditSession[] },
      { scope: AuditScope; id: string }
    >({
      query: ({ scope, id }) => `${auditBase(scope, id)}/audit/sessions`,
      providesTags: (_r, _e, { scope, id }) => [
        { type: 'AuditSession', id: `${scope}:${id}` },
      ],
    }),
    /** Подтверждение выбранных findings — сохранение в слой памяти. */
    confirmAuditSession: b.mutation<
      { saved: number },
      { sessionId: string; scope: AuditScope; entityId: string; findings: AuditFinding[] }
    >({
      query: ({ sessionId, findings }) => ({
        url: `/agents/statut/audit/sessions/${sessionId}/confirm`,
        method: 'POST',
        body: { findings },
      }),
      // Сессию инвалидируем всегда (реальный scope). Источник findings
      // инвалидируем по-разному: для договора/претензии — их карточный тег,
      // чтобы подтянуть обновлённый счётчик HITL; для document-scope
      // (protocol_disagreement / response_claim / outgoing_claim / lawsuit)
      // источник — composed-документ, инвалидируем ComposedResponses:{id}.
      invalidatesTags: (_r, _e, { scope, entityId }) => [
        { type: 'AuditSession', id: `${scope}:${entityId}` },
        scope === 'contract'
          ? { type: 'Contract', id: entityId }
          : scope === 'claim'
            ? { type: 'Claim', id: entityId }
            : { type: 'ComposedResponses', id: entityId },
      ],
    }),
    /** Карточка одного сгенерированного DOCX: метаданные + scope (для
     *  аудита) + обогащённые связи (договор/претензия/иск). Источник данных
     *  страницы /agents/statut/documents/:composedId. Тег ComposedResponses
     *  с id документа — composed_deleted/перегенерация инвалидируют через
     *  generic-тег 'ComposedResponses', а confirmAuditSession для
     *  document-scope бьёт точечно по этому id. */
    getComposedDoc: b.query<ComposedDocDetails, string>({
      query: (id) => `/agents/statut/composed/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'ComposedResponses', id }],
    }),

    annotateClaimRisk: b.mutation<
      { risks: ClaimRisk[] },
      { riskId: string; decision: 'accepted' | 'disputed' | 'rejected'; comment?: string }
    >({
      query: ({ riskId, decision, comment }) => ({
        url: `/agents/statut/claims/risks/${riskId}/annotate`,
        method: 'POST',
        body: { decision, comment },
      }),
      invalidatesTags: ['Claim'],
    }),
    clearClaimRiskAnnotation: b.mutation<{ risks: ClaimRisk[] }, string>({
      query: (riskId) => ({
        url: `/agents/statut/claims/risks/${riskId}/annotate`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Claim'],
    }),
    /** HITL: добавить свой риск к претензии/иску. */
    addManualClaimRisk: b.mutation<
      { risks: ClaimRisk[] },
      {
        claimId: string;
        category: string;
        severity: 'critical' | 'major' | 'minor';
        description: string;
        proposed_action?: string;
      }
    >({
      query: ({ claimId, ...body }) => ({
        url: `/agents/statut/claims/${claimId}/risks`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Claim', id: arg.claimId }],
    }),
    /** HITL: правка текста риска претензии/иска. */
    patchClaimRisk: b.mutation<
      { risks: ClaimRisk[] },
      {
        claimId: string;
        riskId: string;
        category?: string;
        severity?: 'critical' | 'major' | 'minor';
        description?: string;
        proposed_action?: string;
      }
    >({
      query: ({ claimId: _c, riskId, ...body }) => ({
        url: `/agents/statut/claims/risks/${riskId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Claim', id: arg.claimId }],
    }),
    /** HITL: удалить риск претензии/иска. */
    deleteClaimRisk: b.mutation<
      { risks: ClaimRisk[] },
      { claimId: string; riskId: string }
    >({
      query: ({ riskId }) => ({
        url: `/agents/statut/claims/risks/${riskId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Claim', id: arg.claimId }],
    }),
    /** HITL bulk-разметка для рисков претензии/иска. */
    bulkAnnotateClaimRisks: b.mutation<
      { risks: ClaimRisk[]; processed: number; errors: number },
      {
        claimId: string;
        riskIds: string[];
        decision: 'accepted' | 'disputed' | 'rejected';
        comment?: string;
      }
    >({
      query: ({ claimId, riskIds, decision, comment }) => ({
        url: `/agents/statut/claims/${claimId}/risks/bulk-annotate`,
        method: 'POST',
        body: { risk_ids: riskIds, decision, comment },
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Claim', id: arg.claimId }],
    }),
    /** Полное удаление претензии/иска. Каскадно убирает claim_risks и
     *  связанные дедлайны. Кэш `listClaims` инвалидируется автоматически. */
    deleteClaim: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/statut/claims/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Claim', id }, 'Claim', 'Deadline'],
    }),

    // ===== Lawsuits =====
    /** Реестр исков. Backend отдаёт без blob-колонок (manual_*_blob выгружаются
     *  отдельным GET). При WS-событии `lawsuit_composed` / `lawsuit_failed`
     *  тег 'Lawsuit' инвалидируется в rtkBridge — список и карточка сами
     *  подтянут свежее состояние, юристу никаких ручных рефрешей. */
    listLawsuits: b.query<
      { items: Lawsuit[] },
      | {
          /** Фильтр по контрагенту — все иски одного юр.лица. Используется
           *  на странице контрагента «всё по контрагенту». */
          counterparty_id?: string;
          limit?: number;
          offset?: number;
        }
      | void
    >({
      // Без аргумента работает как раньше (params=undefined → весь реестр).
      query: (p) => ({
        url: '/agents/statut/lawsuits',
        params: p ?? undefined,
      }),
      providesTags: ['Lawsuit'],
    }),
    /** Деталка иска: метаданные + история DOCX-черновиков (composed_responses
     *  с lawsuit_id). Тег 'ComposedResponses' подтягивает обновление при
     *  WS-событиях composed_deleted / lawsuit_composed. */
    getLawsuit: b.query<
      {
        lawsuit: Lawsuit;
        composed: Array<{
          id: string;
          claim_id: string | null;
          contract_id: string | null;
          lawsuit_id: string | null;
          template_id: string | null;
          size_bytes: number;
          source_similarity: number | null;
          llm_model: string | null;
          content_hash: string;
          created_at: string;
          source_template_kind: string | null;
        }>;
      },
      string
    >({
      query: (id) => `/agents/statut/lawsuits/${id}`,
      providesTags: (_r, _e, id) => [
        { type: 'Lawsuit', id },
        { type: 'ComposedResponses', id: `lawsuit:${id}` },
      ],
    }),
    /** Создание иска: multipart, обязательное title + (claim_id ИЛИ claim_file)
     *  + (contract_id ИЛИ contract_file опц.). Backend копирует фактуру
     *  в metadata.claim_snapshot — иск не осиротеет при удалении исходной
     *  претензии (FK с ON DELETE SET NULL).
     *
     *  API оборачивает результат в `{ lawsuit: ... }` (симметрично GET
     *  /lawsuits/{id}) — разворачиваем через transformResponse, чтобы
     *  потребители могли работать с плоским Lawsuit без `.lawsuit.id`. */
    createLawsuit: b.mutation<Lawsuit, FormData>({
      query: (form) => ({
        url: '/agents/statut/lawsuits',
        method: 'POST',
        body: form,
      }),
      transformResponse: (response: { lawsuit: Lawsuit }) => response.lawsuit,
      invalidatesTags: ['Lawsuit'],
    }),
    /** Постановка иска в очередь на сборку DOCX. Backend ставит
     *  Job::ComposeLawsuit, возвращает `{status:'queued'}`. Через 30-60с
     *  WS-event `lawsuit_composed` → rtkBridge инвалидирует Lawsuit-тег. */
    composeLawsuit: b.mutation<{ status: 'queued' }, string>({
      query: (id) => ({
        url: `/agents/statut/lawsuits/${id}/compose`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Lawsuit', id }, 'Lawsuit'],
    }),
    /** Удалить иск (cascade composed_responses). После — фронт навигирует
     *  обратно на вкладку «Иски» в реестре. */
    deleteLawsuit: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/statut/lawsuits/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Lawsuit', id },
        'Lawsuit',
        'ComposedResponses',
      ],
    }),

    // ===== Deadlines =====
    listDeadlines: b.query<
      { items: Deadline[] },
      {
        within_days?: number;
        scope?: string;
        kind?: string;
        /** active — ждут исполнения (по умолчанию), overdue — просрочены,
         *  closed — отмечены «выполнено». */
        state?: 'active' | 'overdue' | 'closed';
        limit?: number;
      } | void
    >({
      query: (p) => ({
        url: '/agents/statut/deadlines',
        params: p ?? undefined,
      }),
      providesTags: ['Deadline'],
    }),
    /** Ручное удаление одного срока. Оптимистично убираем строку из всех
     *  активных listDeadlines-кэшей — юрист видит результат мгновенно,
     *  без ожидания ответа сервера. Откат через patch.undo() при ошибке. */
    deleteDeadline: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/statut/deadlines/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        // Патчим ВСЕ варианты listDeadlines (with different filter args).
        const patches: { undo: () => void }[] = [];
        const state = getState() as {
          [statutApi.reducerPath]: { queries: Record<string, unknown> };
        };
        const queries = state[statutApi.reducerPath].queries;
        for (const key of Object.keys(queries)) {
          if (!key.startsWith('listDeadlines(')) continue;
          const entry = queries[key] as { originalArgs?: unknown } | undefined;
          if (!entry) continue;
          const args = entry.originalArgs as Parameters<
            typeof statutApi.endpoints.listDeadlines.initiate
          >[0];
          const patch = dispatch(
            statutApi.util.updateQueryData('listDeadlines', args, (draft) => {
              draft.items = draft.items.filter((d) => d.id !== id);
            }),
          );
          patches.push(patch);
        }
        try {
          await queryFulfilled;
        } catch {
          for (const p of patches) p.undo();
        }
      },
      invalidatesTags: ['Deadline'],
    }),

    /** «Выполнено»: срок исполнен — уходит из активных и просроченных на
     *  вкладку «Выполненные». Карточка договора держит сроки у себя (тег
     *  Contract) — её тоже перечитываем. */
    closeDeadline: b.mutation<{ ok: boolean; status: string }, string>({
      query: (id) => ({
        url: `/agents/statut/deadlines/${id}/close`,
        method: 'POST',
      }),
      invalidatesTags: ['Deadline', 'Contract'],
    }),

    /** Вернуть выполненный срок в работу (отметили по ошибке). */
    reopenDeadline: b.mutation<{ ok: boolean; status: string }, string>({
      query: (id) => ({
        url: `/agents/statut/deadlines/${id}/reopen`,
        method: 'POST',
      }),
      invalidatesTags: ['Deadline', 'Contract'],
    }),

    // ===== Activity =====
    getActivity: b.query<
      {
        items: Array<{
          kind: 'contract_analysis' | 'correspondence_classification' | string;
          title: string;
          id: string;
          started_at: string;
          sender?: string | null;
          subject?: string | null;
        }>;
        count: number;
        sampled_at: string;
      },
      void
    >({
      query: () => '/agents/statut/activity',
    }),

    // ===== LNA =====
    listLna: b.query<
      { items: LnaRecord[]; total: number; limit: number; offset: number },
      {
        search?: string;
        lna_type?: string;
        status?: string;
        owner_department?: string;
        sort?: string;
        order?: 'asc' | 'desc';
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (p) => ({
        url: '/agents/statut/lna',
        params: p ?? undefined,
      }),
      providesTags: ['Lna'],
    }),
    createLna: b.mutation<
      LnaRecord,
      {
        title: string;
        lna_type: string;
        effective_from?: string;
        expires_at?: string;
        status?: string;
        description?: string;
        content_text?: string;
        owner_department?: string;
        visible_in_assistant?: boolean;
      }
    >({
      query: (body) => ({
        url: '/agents/statut/lna',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Lna'],
    }),
    updateLnaStatus: b.mutation<{ ok: boolean }, { id: string; status: string }>({
      query: ({ id, status }) => ({
        url: `/agents/statut/lna/${id}`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: ['Lna'],
    }),
    updateLnaVisibility: b.mutation<
      { ok: boolean },
      { id: string; visible_in_assistant: boolean }
    >({
      query: ({ id, visible_in_assistant }) => ({
        url: `/agents/statut/lna/${id}/visibility`,
        method: 'PATCH',
        body: { visible_in_assistant },
      }),
      invalidatesTags: ['Lna'],
    }),
    deleteLna: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/statut/lna/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Lna'],
    }),

    /** Безопасный паспорт (сводка) договора для смежных служб (`/contracts/:id/brief`). */
    getContractBrief: b.query<ContractBrief, string>({
      query: (id) => `/agents/statut/contracts/${id}/brief`,
      providesTags: (_r, _e, id) => [
        { type: 'Contract', id },
        { type: 'ContractHandoff', id },
      ],
    }),

    /** Обратная связь профильного отдела по разосланному перечню рисков договора. */
    respondToRiskDispatch: b.mutation<
      { ok: boolean; status: string },
      { targetId: string; status: 'accepted' | 'disputed' | 'mitigated'; comment?: string }
    >({
      query: ({ targetId, status, comment }) => ({
        url: `/agents/statut/risk-dispatch-targets/${targetId}/respond`,
        method: 'POST',
        body: { status, comment },
      }),
      invalidatesTags: ['RiskNotification'],
    }),

    // ===== Госорганы =====
    listGov: b.query<
      { items: GovRequest[]; total: number; limit: number; offset: number },
      {
        search?: string;
        authority?: string;
        status?: string;
        priority?: string;
        sort?: string;
        order?: 'asc' | 'desc';
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (p) => ({
        url: '/agents/statut/gov-requests',
        params: p ?? undefined,
      }),
      providesTags: ['GovRequest'],
    }),
    createGov: b.mutation<
      GovRequest,
      {
        authority: string;
        request_no?: string;
        subject: string;
        content_text?: string;
        received_at?: string;
        deadline?: string;
        priority?: string;
        is_inspection?: boolean;
      }
    >({
      query: (body) => ({
        url: '/agents/statut/gov-requests',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['GovRequest', 'Deadline'],
    }),
    getGov: b.query<GovRequest, string>({
      query: (id) => `/agents/statut/gov-requests/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'GovRequest', id }],
    }),
    patchGov: b.mutation<
      { ok: boolean },
      {
        id: string;
        status?: string;
        response_text?: string;
        legality_check?: string;
        notes?: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/statut/gov-requests/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'GovRequest', id }, 'GovRequest'],
    }),
    markGovSent: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/statut/gov-requests/${id}/send`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'GovRequest', id }, 'GovRequest'],
    }),

    // ===== Мониторинг НПА =====
    listNpa: b.query<
      { items: NpaEvent[]; total: number; limit: number; offset: number },
      {
        search?: string;
        source?: string;
        event_type?: string;
        severity?: string;
        since_days?: number;
        sort?: string;
        order?: 'asc' | 'desc';
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (p) => ({
        url: '/agents/statut/npa-events',
        params: p ?? undefined,
      }),
      providesTags: ['NpaEvent'],
    }),
    createNpaEvent: b.mutation<
      NpaEvent,
      {
        source: string;
        event_type: string;
        title: string;
        summary?: string;
        url?: string;
        occurred_at?: string;
        severity?: string;
        tags?: string[];
      }
    >({
      query: (body) => ({
        url: '/agents/statut/npa-events',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['NpaEvent'],
    }),
    getNpaDigest: b.query<{ days: number; counts: NpaDigestCounts }, { days?: number } | void>({
      query: (p) => ({
        url: '/agents/statut/npa-events/digest',
        params: p ?? undefined,
      }),
      providesTags: ['NpaEvent'],
    }),

    /** Агрегированный snapshot для StatutDashboard. Заменяет 12
     *  параллельных listing-query'ев одним SQL с готовыми counts.
     *  Это убирает frontend jank (12 JSON.parse + 12 re-render'ов
     *  на main thread = 600ms+) и backend CPU starvation (12 auth-
     *  check + 12 SQL listing). */
    getDashboardSnapshot: b.query<DashboardSnapshot, void>({
      query: () => ({ url: '/agents/statut/dashboard/snapshot' }),
      // Инвалидируется любым изменением Claim/Contract/Correspondence/
      // и т.д. — чтобы при загрузке нового договора снэпшот обновился.
      providesTags: [
        'Claim',
        'Contract',
        'Correspondence',
        'Counterparty',
        'Deadline',
        'Check',
        'Knowledge',
        'Lna',
        'GovRequest',
      ],
    }),

    // ===== Калькулятор =====
    calcPenalty: b.mutation<
      {
        total: number;
        days: number;
        rate_used_pct: number;
        formula: string;
        note: string;
      },
      {
        mode: 'art_395' | 'contractual';
        principal: number;
        from: string;
        to: string;
        rate_per_day_pct?: number;
      }
    >({
      query: (body) => ({
        url: '/agents/statut/calculator/penalty',
        method: 'POST',
        body,
      }),
    }),

    /** Текущая ключевая ставка ЦБ РФ (подтягивается фоном из веб-сервиса ЦБ). */
    getKeyRate: b.query<
      {
        current: {
          effective_date: string;
          rate_pct: number;
          fetched_at?: string;
        } | null;
      },
      void
    >({
      query: () => ({ url: '/agents/statut/calculator/key-rate' }),
    }),

    /** §8.3 Stage 4: привязать проект к договору Статута (мост для КС-2). */
    setProjectContract: b.mutation<
      { project: PtoProject },
      { projectId: string; contract_id: string | null }
    >({
      query: ({ projectId, contract_id }) => ({
        url: `/agents/pto/projects/${projectId}/contract`,
        method: 'PATCH',
        body: { contract_id },
      }),
      invalidatesTags: ['PtoProject'],
    }),

    /** Привязать договор Юриста к строительному объекту ПТО (`pto_projects.id`). */
    setContractProject: b.mutation<
      { ok: boolean; contract_id: string; project_id: string | null },
      { contractId: string; project_id: string | null }
    >({
      query: ({ contractId, project_id }) => ({
        url: `/agents/statut/contracts/${contractId}/project`,
        method: 'PUT',
        body: { project_id },
      }),
      invalidatesTags: (_r, _e, { contractId }) => [
        { type: 'Contract', id: contractId },
        'Contract',
      ],
    }),

    /** Привязать претензию Юриста к строительному объекту ПТО (`pto_projects.id`). */
    setClaimProject: b.mutation<
      { ok: boolean; claim_id: string; project_id: string | null },
      { claimId: string; project_id: string | null }
    >({
      query: ({ claimId, project_id }) => ({
        url: `/agents/statut/claims/${claimId}/project`,
        method: 'PUT',
        body: { project_id },
      }),
      invalidatesTags: (_r, _e, { claimId }) => [{ type: 'Claim', id: claimId }, 'Claim'],
    }),

    /** Привязать судебный иск Юриста к строительному объекту ПТО (`pto_projects.id`). */
    setLawsuitProject: b.mutation<
      { ok: boolean; lawsuit_id: string; project_id: string | null },
      { lawsuitId: string; project_id: string | null }
    >({
      query: ({ lawsuitId, project_id }) => ({
        url: `/agents/statut/lawsuits/${lawsuitId}/project`,
        method: 'PUT',
        body: { project_id },
      }),
      invalidatesTags: (_r, _e, { lawsuitId }) => [{ type: 'Lawsuit', id: lawsuitId }, 'Lawsuit'],
    }),

    /** Привязать письмо к строительному объекту ПТО (`pto_projects.id`). */
    setCorrespondenceProject: b.mutation<
      { ok: boolean; correspondence_id: string; project_id: string | null },
      { correspondenceId: string; project_id: string | null }
    >({
      query: ({ correspondenceId, project_id }) => ({
        url: `/agents/statut/correspondence/${correspondenceId}/project`,
        method: 'PUT',
        body: { project_id },
      }),
      invalidatesTags: (_r, _e, { correspondenceId }) => [
        { type: 'Correspondence', id: correspondenceId },
        'Correspondence',
      ],
    }),

    /** Перенаправить файл комплекта договора в профильный модуль (МТО, Сметчик, КСП, ПТО, Экономист). */
    routeContractVersionFile: b.mutation<
      {
        ok: boolean;
        contract_id: string;
        file_id: string;
        routed_agent: string;
        routed_entity_id: string | null;
      },
      { contractId: string; versionId: string; fileId: string; target_agent?: string }
    >({
      query: ({ contractId, versionId, fileId, target_agent }) => ({
        url: `/agents/statut/contracts/${contractId}/versions/${versionId}/files/${fileId}/route`,
        method: 'POST',
        body: target_agent ? { target_agent } : {},
      }),
      invalidatesTags: (_r, _e, { contractId }) => [
        { type: 'Contract', id: contractId },
        { type: 'ContractFiles', id: contractId },
      ],
    }),

    /** Список активных строительных объектов для привязки договоров, претензий, исков и писем в ОСНОВА.Юрист. */
    listStatutProjects: b.query<
      { items: Array<{ id: string; name: string; code: string | null; status: string }> },
      void
    >({
      query: () => '/agents/statut/projects',
      providesTags: ['PtoProject'],
    }),

    /** Активировать отложенное условное обязательство (`execution_terms`) при наступлении триггер-события. */
    triggerExecutionTerm: b.mutation<
      { ok: boolean; deadline_id: string; due_at: string; term_id: string },
      { contractId: string; termId: string; event_date?: string; note?: string }
    >({
      query: ({ contractId, termId, event_date, note }) => ({
        url: `/agents/statut/contracts/${contractId}/execution-terms/${termId}/trigger`,
        method: 'POST',
        body: { event_date, note },
      }),
      invalidatesTags: (_r, _e, { contractId }) => [
        { type: 'Contract', id: contractId },
        'Deadline',
      ],
    }),

    /** Список отправленных рассылок рисков договора по отделам и статусов их обратной связи. */
    listContractRiskDispatches: b.query<{ items: RiskDispatchTarget[] }, string>({
      query: (contractId) => `/agents/statut/contracts/${contractId}/risk-dispatches`,
      providesTags: (_r, _e, contractId) => [
        { type: 'Contract', id: contractId },
        'RiskNotification',
      ],
    }),

    /** Автогенерация черновика официального письма по сигналу отставания вехи из КСП. */
    createCorrespondenceFromKsp: b.mutation<
      { correspondence: Correspondence },
      {
        project_id?: string | null;
        contract_id?: string | null;
        counterparty: string;
        milestone_title: string;
        delay_days: number;
        reason?: string;
      }
    >({
      query: (body) => ({
        url: '/agents/statut/correspondence/from-ksp',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Correspondence'],
    }),

    /** Автоматическая инициация исходящей претензии по сигналу из смежного модуля с расчётом неустойки. */
    initiateClaimFromSignal: b.mutation<
      { claim: Claim; penalty_amount: number; total_amount: number | null },
      {
        source_agent: string;
        counterparty: string;
        contract_id?: string | null;
        project_id?: string | null;
        dispute_type?: string;
        principal?: number;
        overdue_from?: string;
        overdue_to?: string;
        daily_rate_percent?: number;
        description: string;
      }
    >({
      query: (body) => ({
        url: '/agents/statut/claims/initiate-from-signal',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Claim'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useSetProjectContractMutation,
  useSetContractProjectMutation,
  useSetClaimProjectMutation,
  useSetLawsuitProjectMutation,
  useSetCorrespondenceProjectMutation,
  useRouteContractVersionFileMutation,
  useListStatutProjectsQuery,
  useTriggerExecutionTermMutation,
  useListContractRiskDispatchesQuery,
  useCreateCorrespondenceFromKspMutation,
  useInitiateClaimFromSignalMutation,
  useListCorrespondenceQuery,
  useGetCorrespondenceQuery,
  useIntakeCorrespondenceMutation,
  useReturnCorrespondenceMutation,
  useRegenerateCorrespondenceDraftMutation,
  useUnreturnCorrespondenceMutation,
  useSetCorrespondenceContractMutation,
  useListContractsQuery,
  useGetContractQuery,
  useGetContractBriefQuery,
  useGetContractDetailsQuery,
  useCreateContractMutation,
  useConcludeContractMutation,
  useUnarchiveContractMutation,
  useAppendFilesToVersionMutation,
  useUploadContractMutation,
  useUploadContractVersionMutation,
  useDeleteContractVersionMutation,
  useUploadFilesToContractMutation,
  useListContractVersionFilesQuery,
  useDeleteContractVersionFileMutation,
  useSetContractVersionFileKindMutation,
  useListContractPartiesQuery,
  useAnalyzeContractMutation,
  useForceAnalyzeContractMutation,
  useCancelContractAnalysisMutation,
  useDiffContractVersionsQuery,
  useLazyDiffContractVersionsQuery,
  useListContractTemplatesQuery,
  useCreateContractTemplateMutation,
  useUploadContractTemplateMutation,
  useDeleteContractTemplateMutation,
  useListCounterpartiesQuery,
  useGetCounterpartyQuery,
  useGetCounterpartyDetailsQuery,
  useCreateCounterpartyMutation,
  useRefreshCounterpartyEgrulMutation,
  useRefreshCounterpartyExternalMutation,
  useGetCounterpartyRiskMapQuery,
  useUpdateCounterpartyNotesMutation,
  useGetCounterpartyDocumentsQuery,
  useUploadCounterpartyDocumentsMutation,
  useDeleteCounterpartyDocumentMutation,
  useGetCounterpartyRequisiteCheckQuery,
  useLazyGetCounterpartyRequisiteCheckQuery,
  useLookupCounterpartyByInnQuery,
  useLazyLookupCounterpartyByInnQuery,
  useGetStatutMetricsQuery,
  useGetContractAuditQuery,
  useAnnotateRiskMutation,
  useClearRiskAnnotationMutation,
  useAddManualContractRiskMutation,
  usePatchContractRiskMutation,
  useDeleteContractRiskMutation,
  useBulkAnnotateContractRisksMutation,
  usePatchContractMutation,
  useUpdateContractNoteMutation,
  useDispatchContractRisksMutation,
  useListRiskNotificationsQuery,
  useMarkRiskNotificationSeenMutation,
  useRespondToRiskDispatchMutation,
  useSetContractNumberMutation,
  useGetContractCorrespondenceQuery,
  useDeleteContractMutation,
  useGetActivityQuery,
  useListClaimsQuery,
  useGetClaimQuery,
  useGetClaimDetailsQuery,
  useListClaimRisksQuery,
  useCreateClaimMutation,
  useUpdateClaimStageMutation,
  useUploadClaimMutation,
  useReanalyzeClaimMutation,
  useSetClaimDirectionMutation,
  useLinkClaimContractMutation,
  useUnlinkClaimContractMutation,
  useRedirectClaimPreviewMutation,
  useComposeClaimRedirectMutation,
  useComposeContractProtocolMutation,
  useListComposedForContractQuery,
  useRedirectClaimSaveMutation,
  useUploadDocumentsToClaimMutation,
  useListClaimDocumentsQuery,
  useDeleteClaimDocumentMutation,
  useComposeClaimResponseMutation,
  useListComposedResponsesQuery,
  useListComposedArchiveQuery,
  useGetComposedDocQuery,
  useDeleteComposedMutation,
  useListClaimPartiesQuery,
  useDeleteClaimMutation,
  useListLawsuitsQuery,
  useGetLawsuitQuery,
  useCreateLawsuitMutation,
  useComposeLawsuitMutation,
  useDeleteLawsuitMutation,
  useAnnotateClaimRiskMutation,
  useClearClaimRiskAnnotationMutation,
  useAddManualClaimRiskMutation,
  usePatchClaimRiskMutation,
  useDeleteClaimRiskMutation,
  useBulkAnnotateClaimRisksMutation,
  useGetAuditPackageQuery,
  useStartAuditSessionMutation,
  useListAuditSessionsQuery,
  useConfirmAuditSessionMutation,
  useListDeadlinesQuery,
  useDeleteDeadlineMutation,
  useCloseDeadlineMutation,
  useReopenDeadlineMutation,
  useGetCorrespondenceDetailsQuery,
  useListLnaQuery,
  useCreateLnaMutation,
  useUpdateLnaStatusMutation,
  useUpdateLnaVisibilityMutation,
  useDeleteLnaMutation,
  useListGovQuery,
  useCreateGovMutation,
  useGetGovQuery,
  usePatchGovMutation,
  useMarkGovSentMutation,
  useListNpaQuery,
  useCreateNpaEventMutation,
  useGetNpaDigestQuery,
  useGetDashboardSnapshotQuery,
  useCalcPenaltyMutation,
  useGetKeyRateQuery,
} = statutApi;
